import classNames from 'classnames'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import BSCard from 'react-bootstrap/Card'

import Card from '@/components/Card/Card'
import Mana, { type ManaType } from '@/components/Mana/Mana'
import LinkHelper from '@/components/Panels/LinkHelper/LinkHelper'
import { CardLabelVariantType, PageCardType } from '@/types/card'
import getParams from '@/utils/getParams'
import useDirectories from '@/utils/useDirectories'

import styles from './CardPanel.module.scss'

const COLOR_ORDER = [
  { slug: 'colorless', symbol: 'c' },
  { slug: 'mono-white', symbol: 'w' },
  { slug: 'mono-blue', symbol: 'u' },
  { slug: 'mono-black', symbol: 'b' },
  { slug: 'mono-red', symbol: 'r' },
  { slug: 'mono-green', symbol: 'g' },
  { slug: 'azorius', symbol: 'wu' },
  { slug: 'dimir', symbol: 'ub' },
  { slug: 'rakdos', symbol: 'br' },
  { slug: 'gruul', symbol: 'rg' },
  { slug: 'selesnya', symbol: 'gw' },
  { slug: 'orzhov', symbol: 'wb' },
  { slug: 'izzet', symbol: 'ur' },
  { slug: 'golgari', symbol: 'bg' },
  { slug: 'boros', symbol: 'rw' },
  { slug: 'simic', symbol: 'gu' },
  { slug: 'esper', symbol: 'wub' },
  { slug: 'grixis', symbol: 'ubr' },
  { slug: 'jund', symbol: 'brg' },
  { slug: 'naya', symbol: 'rgw' },
  { slug: 'bant', symbol: 'gwu' },
  { slug: 'abzan', symbol: 'wbg' },
  { slug: 'jeskai', symbol: 'urw' },
  { slug: 'sultai', symbol: 'bgu' },
  { slug: 'mardu', symbol: 'rwb' },
  { slug: 'temur', symbol: 'gur' },
  { slug: 'yore-tiller', symbol: 'wubr' },
  { slug: 'glint-eye', symbol: 'ubrg' },
  { slug: 'dune-brood', symbol: 'brgw' },
  { slug: 'ink-treader', symbol: 'rgwu' },
  { slug: 'witch-maw', symbol: 'gwub' },
  { slug: 'five-color', symbol: 'wubrg' },
] as const

type ColorSlug = (typeof COLOR_ORDER)[number]['slug']

type ColorLink = {
  slug: string
}

const COLOR_ORDER_INDEX = Object.fromEntries(COLOR_ORDER.map(({ slug }, index) => [slug, index])) as Record<
  ColorSlug,
  number
>

const COLOR_SYMBOL_BY_SLUG = Object.fromEntries(COLOR_ORDER.map(({ slug, symbol }) => [slug, symbol])) as Record<
  ColorSlug,
  ManaType
>

const isColorSlug = (slug: string): slug is ColorSlug => slug in COLOR_ORDER_INDEX

const getColorSymbol = (slug: string): ManaType | undefined =>
  isColorSlug(slug) ? COLOR_SYMBOL_BY_SLUG[slug] : undefined

const sortColorLinks = (links: ColorLink[]) =>
  [...links].sort((a, b) => {
    const aIndex = isColorSlug(a.slug) ? COLOR_ORDER_INDEX[a.slug] : Number.MAX_SAFE_INTEGER
    const bIndex = isColorSlug(b.slug) ? COLOR_ORDER_INDEX[b.slug] : Number.MAX_SAFE_INTEGER
    return aIndex - bIndex
  })

type Props = {
  card: PageCardType
  className?: string
  colorlinks?: ColorLink[]
  labelVariant?: CardLabelVariantType
}

const CardPanel = ({ card, className, colorlinks, labelVariant }: Props) => {
  const directories = useDirectories()
  const sortedColorLinks = colorlinks ? sortColorLinks(colorlinks) : []

  const { bracket, budget, colorId, tag } = directories.dir !== 'precon' ? getParams(directories) : {}
  const linkPrefix = `/${directories.dir}/${card.sanitized}`
  const linkSuffix = `${bracket ? `/${bracket}` : ''}${tag ? `/${tag}` : ''}${budget ? `/${budget}` : ''}`

  return (
    <BSCard className={classNames(styles.container, className, 'shadow-sm')}>
      {colorlinks && (
        <div className='mb-2'>
          <div className={styles.navigationTitle}>Color Identity</div>
          <ButtonGroup className={styles.navigationButtons} size='sm'>
            <LinkHelper href={`${linkPrefix}${linkSuffix}`} primary={colorId === undefined} scroll={false}>
              All
            </LinkHelper>
            {sortedColorLinks.map(({ slug }) => {
              return (
                <LinkHelper
                  href={`${linkPrefix}/${slug}${linkSuffix}`}
                  key={slug}
                  primary={colorId === slug}
                  scroll={false}
                >
                  <span className='align-items-center d-inline-flex gap-1'>
                    <Mana color={getColorSymbol(slug)} />
                  </span>
                </LinkHelper>
              )
            })}
          </ButtonGroup>
        </div>
      )}
      <Card card={{ ...card, url: card.url || '' }} fullCard={card} labelVariant={labelVariant} pageCommander={card} />
    </BSCard>
  )
}

export default CardPanel
